import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { ApiError, type Chefe, type EventoAtual, type GmMob, type GmOnline, type GmOnlinePagina, type MobResumo } from '../api/types';
import { idiomaAtual, t } from '../i18n/idioma';
import { nomeDoInimigo } from '../i18n/nomes';
import { carregarCatalogo, mapaPronto, type RegistroMapa } from '../world/mapa';
import { formatarHora, minutoAgora } from '../world/relogio';
import { GmSkins } from './GmSkins';
import { MarketGump } from './MarketGump';
import { agoraHHMM, contagemCurta, IconeGm, idDaCidade, useListasDoGm, type Feito, type JogadorSalvo, type ParaRegistro, type Rodar } from './gm/comum';
import { EventosGm } from './gm/Eventos';
import type { Penta } from '../api/evento';
import { FichaDoJogador, LIMITE_BUSCA, ListaDeJogadores, MIN_BUSCA, type Fonte } from './gm/Jogadores';
import { MundoGm } from './gm/Mundo';
import { PainelGm } from './gm/Painel';
import { PaletaGm, type AcaoNoJogador, type Secao } from './gm/Paleta';

interface Props {
  active: boolean;
  youName: string;
  role?: string;
  shard?: string;
  canal?: number;
  oculto?: boolean;
  onOculto?: (ligado: boolean) => void;
  onPickTile: (hint?: string) => Promise<{ x: number; y: number } | null>;
  onCancelPick: () => void;
  onTeleportedSelf: (x: number, y: number) => void;
  onChangeCity: (cityId: string) => Promise<void>;
  targetMobId: number | null;
  tileX?: number;
  tileY?: number;
  onSpawned?: (mob: GmMob, hits: number) => void;
  onRemoved?: (shortId: number) => void;
  skillsTick?: number;
  packTick?: number;
  onPackChanged?: () => void;
  onEditarSkills?: (alvo: { id: string; name: string }) => void;
}

const SECOES: readonly Secao[] = ['painel', 'jogadores', 'mundo', 'eventos', 'itens', 'skins', 'registro'];
/** A sala do GM: só a primeira página curta; o resto do shard nunca é baixado (techspec-gm-menu §03). */
const TAKE_SALA = 25;
const POLL_MS = 4000;
const POLL_TOTAL_MS = 30000;
const AVISO_MS = 6000;

interface Aviso { readonly id: number; readonly feito: Feito }

const msgDe = (err: unknown) => (err instanceof ApiError ? err.message : t('comum.recusouPedido'));
const ehJogador = (v: unknown): v is GmOnline =>
  !!v && typeof v === 'object' && 'id' in v && 'notoriety' in v && 'name' in v;

/**
 * O menu do GM (techspec-gm-menu): trilho com 7 seções, busca pelo nome no servidor, ficha do jogador, confirmação
 * nas ações perigosas, Registro da sessão com Desfazer onde é barato e a paleta (Ctrl+K).
 */
export function GmGump({
  active, youName, role, shard, canal, oculto = false, onOculto,
  onPickTile, onCancelPick, onTeleportedSelf, onChangeCity,
  targetMobId, tileX, tileY, onSpawned, onRemoved, skillsTick = 0, packTick = 0, onPackChanged, onEditarSkills
}: Props) {
  const idioma = idiomaAtual();
  const [secao, setSecao] = useState<Secao>('painel');
  const [paleta, setPaleta] = useState(false);

  const [busca, setBusca] = useState('');
  const [buscaDeb, setBuscaDeb] = useState('');
  const [achados, setAchados] = useState<GmOnlinePagina | null>(null);
  const [sala, setSala] = useState<GmOnlinePagina | null>(null);
  const [fonte, setFonte] = useState<Fonte>('sala');
  const [totalOnline, setTotalOnline] = useState<number | null>(null);
  const [ocultoHabilitado, setOcultoHabilitado] = useState(true);
  const [eu, setEu] = useState<GmOnline | null>(null);
  const [picked, setPicked] = useState<GmOnline | null>(null);
  const [offline, setOffline] = useState(false);
  const [itensPara, setItensPara] = useState<GmOnline | null>(null);
  const [itemBusca, setItemBusca] = useState<{ texto: string } | undefined>(undefined);

  const [mobs, setMobs] = useState<readonly GmMob[]>([]);
  const [catalogo, setCatalogo] = useState<readonly MobResumo[]>([]);
  const [evAtual, setEvAtual] = useState<EventoAtual | null>(null);
  const [chefe, setChefe] = useState<Chefe | null>(null);
  const [penta, setPenta] = useState<Penta | null>(null);
  const [cidades, setCidades] = useState<readonly RegistroMapa[]>([]);
  const [minuto, setMinuto] = useState(() => Math.floor(minutoAgora()));

  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [picking, setPicking] = useState<'none' | 'teleport' | 'summon'>('none');
  const [registro, setRegistro] = useState<readonly Feito[]>([]);
  const [avisos, setAvisos] = useState<readonly Aviso[]>([]);
  const [copiado, setCopiado] = useState(false);
  const seq = useRef(0);

  const { recentes, fixados, tocar, alternarFixado } = useListasDoGm();

  // Os refs deixam o polling ler o estado atual sem reiniciar o intervalo a cada mudança.
  const pickedRef = useRef(picked);
  pickedRef.current = picked;
  const buscaRef = useRef(buscaDeb);
  buscaRef.current = buscaDeb;

  /* ---------- carga ---------- */

  const atualizarEscolhido = async (pSala: GmOnlinePagina | null, pAchados: GmOnlinePagina | null) => {
    const cur = pickedRef.current;
    if (!cur) return;
    let achou = pSala?.itens.find(p => p.id === cur.id) ?? pAchados?.itens.find(p => p.id === cur.id) ?? null;
    if (!achou) {
      // Fora das páginas curtas: uma busca pelo nome, só deste jogador.
      const p = await api.listGmOnline(cur.name, 5).catch(() => null);
      if (!p) return;
      achou = p.itens.find(x => x.id === cur.id) ?? null;
    }
    if (pickedRef.current?.id !== cur.id) return;
    if (achou) { setPicked(achou); setOffline(false); } else setOffline(true);
  };

  const reload = async () => {
    try {
      const q = buscaRef.current;
      const [pSala, pAchados, nextMobs, ev] = await Promise.all([
        api.listGmOnline(undefined, TAKE_SALA, true),
        q.length >= MIN_BUSCA ? api.listGmOnline(q, LIMITE_BUSCA) : Promise.resolve(null),
        api.listGmMobs(),
        api.eventoAtual().catch((): EventoAtual | null => null)
      ]);
      void api.eventoChefe().then(setChefe).catch(() => { /* o card do chefe fica sem dados */ });
      void api.eventoPentagrama().then(setPenta).catch(() => { /* o card do pentagrama fica sem dados */ });
      setSala(pSala);
      if (q === buscaRef.current) setAchados(pAchados);
      setOcultoHabilitado(pSala.ocultoHabilitado !== false);
      const me = pSala.itens.find(p => p.you) ?? pAchados?.itens.find(p => p.you) ?? null;
      if (me) { setEu(me); onOculto?.(!!me.oculto); }
      setMobs(nextMobs);
      setEvAtual(ev);
      setMinuto(Math.floor(minutoAgora()));
      await atualizarEscolhido(pSala, pAchados);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t('gm.naoLeuOnline'));
    }
  };

  const lerTotal = () => {
    void api.listGmOnline(undefined, 1).then(p => setTotalOnline(p.total)).catch(() => { /* o contador fica "—" */ });
  };

  useEffect(() => {
    const id = window.setTimeout(() => setBuscaDeb(busca.trim()), 250);
    return () => window.clearTimeout(id);
  }, [busca]);

  useEffect(() => {
    if (buscaDeb.length < MIN_BUSCA) { setAchados(null); return; }
    if (!active) return;
    let vivo = true;
    setAchados(null);
    api.listGmOnline(buscaDeb, LIMITE_BUSCA)
      .then(p => { if (vivo) setAchados(p); })
      .catch(err => { if (vivo) setErro(msgDe(err)); });
    return () => { vivo = false; };
  }, [buscaDeb, active]);

  useEffect(() => {
    void carregarCatalogo().then(l => setCidades(l.filter(mapaPronto))).catch(() => setCidades([]));
    void api.listMobCatalog().then(setCatalogo).catch(() => setCatalogo([]));
  }, []);

  useEffect(() => {
    if (!active) {
      setPicking('none');
      setPaleta(false);
      onCancelPick();
      return;
    }
    void reload();
    lerTotal();
    const a = window.setInterval(() => { void reload(); }, POLL_MS);
    const b = window.setInterval(lerTotal, POLL_TOTAL_MS);
    const c = window.setInterval(() => setMinuto(Math.floor(minutoAgora())), 5000);
    return () => { window.clearInterval(a); window.clearInterval(b); window.clearInterval(c); };
  }, [active]);

  /* ---------- ações ---------- */

  const avisar = (feito: Feito) => {
    const id = feito.id;
    setAvisos(l => [...l.slice(-2), { id, feito }]);
    window.setTimeout(() => setAvisos(l => l.filter(a => a.id !== id)), AVISO_MS);
  };

  const registrar = (reg: ParaRegistro) => {
    const feito: Feito = { id: ++seq.current, hora: agoraHHMM(), cat: reg.cat, txt: reg.txt, desfazer: reg.desfazer ?? null };
    setRegistro(l => [feito, ...l].slice(0, 200));
    avisar(feito);
  };

  const rodar: Rodar = async (fn, reg) => {
    setBusy(true);
    setErro(null);
    try {
      const r = await fn();
      if (ehJogador(r)) setPicked(cur => (cur && cur.id === r.id ? r : cur));
      if (reg) {
        registrar(reg);
        if (reg.alvo && !reg.alvo.you) tocar(reg.alvo);
      }
      void reload();
      return true;
    } catch (err) {
      setErro(msgDe(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const desfazer = async (f: Feito) => {
    if (!f.desfazer) return;
    const fn = f.desfazer;
    const ok = await rodar(fn, { cat: f.cat, txt: t('gm.x.reg.desfez', { txt: f.txt }) });
    if (ok) {
      setRegistro(l => l.map(x => (x.id === f.id ? { ...x, desfazer: null } : x)));
      setAvisos(l => l.filter(a => a.id !== f.id));
    }
  };

  const escolher = (j: GmOnline) => {
    setPicked(j);
    setOffline(false);
    if (!j.you) tocar(j);
    setSecao('jogadores');
  };

  const escolherSalvo = async (j: JogadorSalvo) => {
    setSecao('jogadores');
    const p = await api.listGmOnline(j.name, 5).catch(() => null);
    const achou = p?.itens.find(x => x.id === j.id) ?? null;
    if (achou) { escolher(achou); return; }
    setPicked({ id: j.id, name: j.name, city: '—', dead: false, you: false, notoriety: 'inocente' });
    setOffline(true);
  };

  const irAte = (alvo: GmOnline) => void rodar(async () => {
    const d = await api.gmIr(alvo.id);
    if (eu && d.cidade === eu.city) onTeleportedSelf(d.x, d.y);
  }, { cat: t('gm.x.cat.mover'), txt: t('gm.x.reg.foiAte', { nome: alvo.name }), alvo });

  const puxar = (alvo: GmOnline) => {
    if (tileX == null || tileY == null) return;
    void rodar(() => api.gmTeleport(alvo.id, tileX, tileY), { cat: t('gm.x.cat.mover'), txt: t('gm.x.reg.puxou', { nome: alvo.name }), alvo });
  };

  const cancelarPick = () => { setPicking('none'); onCancelPick(); };

  const teleportar = async (alvo: GmOnline) => {
    setPicking('teleport');
    setErro(null);
    const tile = await onPickTile();
    setPicking('none');
    if (!tile) return;
    await rodar(async () => {
      await api.gmTeleport(alvo.id, tile.x, tile.y);
      if (alvo.you) onTeleportedSelf(tile.x, tile.y);
    }, { cat: t('gm.x.cat.mover'), txt: t('gm.x.reg.teleportou', { nome: alvo.name, x: tile.x, y: tile.y }), alvo });
  };

  const mandar = (alvo: GmOnline, cidadeId: string) => {
    const antes = idDaCidade(alvo.city, cidades);
    const nomeCidade = cidades.find(c => c.id === cidadeId)?.nome ?? cidadeId;
    void rodar(async () => {
      const next = await api.gmCity(alvo.id, cidadeId);
      setPicked(cur => (cur && cur.id === next.id ? next : cur));
      if (alvo.you) await onChangeCity(cidadeId);
    }, {
      cat: t('gm.x.cat.mover'),
      txt: t('gm.x.reg.mandou', { nome: alvo.name, cidade: nomeCidade }),
      desfazer: antes && antes !== cidadeId
        ? async () => { await api.gmCity(alvo.id, antes); if (alvo.you) await onChangeCity(antes); }
        : undefined,
      alvo
    });
  };

  const invocar = async (defId: string, alcance: number | undefined) => {
    setPicking('summon');
    setErro(null);
    const tile = await onPickTile(t('gm.cliqueParaInvocar'));
    setPicking('none');
    if (!tile) return;
    const def = catalogo.find(c => c.defId === defId);
    let criado: GmMob | null = null;
    await rodar(async () => {
      const mob = await api.gmSpawnMob(defId, tile.x, tile.y, alcance);
      criado = mob;
      onSpawned?.(mob, def?.hits ?? 46);
    }, {
      cat: t('gm.x.cat.mundo'),
      txt: t('gm.x.reg.invocou', { nome: nomeDoInimigo(defId, def?.nome ?? defId), x: tile.x, y: tile.y }),
      desfazer: async () => {
        if (!criado) return;
        const id = (criado as GmMob).shortId;
        await api.gmRemoveMob(id);
        onRemoved?.(id);
      }
    });
  };

  const alternarOculto = () => void rodar(async () => {
    const n = await api.gmOculto(!oculto);
    onOculto?.(n.ligado);
  }, { cat: t('gm.x.cat.jogador'), txt: t(oculto ? 'gm.x.reg.visivel' : 'gm.x.reg.oculto') });

  const naPaleta = (acao: AcaoNoJogador, j: GmOnline) => {
    if (acao === 'ficha') escolher(j);
    else if (acao === 'ir') irAte(j);
    else if (acao === 'puxar') puxar(j);
    else void rodar(() => api.gmRevive(j.id), { cat: t('gm.x.cat.jogador'), txt: t('gm.x.reg.reviveu', { nome: j.name }), alvo: j });
  };

  const copiarRegistro = async () => {
    const texto = registro.slice().reverse().map(f => `${f.hora} [${f.cat}] ${f.txt}`).join('\n');
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      setErro(t('gm.x.reg.naoCopiou'));
    }
  };

  /* ---------- teclado: Ctrl+K e Ctrl+H com a janela aberta (os números ficam com a barra de skills) ---------- */

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
      const k = e.key.toLowerCase();
      if (k === 'k') { e.preventDefault(); setPaleta(p => !p); }
      if (k === 'h' && ocultoHabilitado) { e.preventDefault(); alternarOculto(); }
    };
    // Na captura: os atalhos do jogo engolem Ctrl+K/Ctrl+H do navegador na captura da window (input/atalhos.ts).
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  /* ---------- tela ---------- */

  const hora = formatarHora(minuto, idioma);
  const ubAberto = !!evAtual && evAtual.estado !== 0;
  const chefeVivo = !!(chefe?.aoVivo && chefe.aoVivo.estado === 0);
  const eventoAoVivo = chefeVivo ? chefe!.aoVivo!.nome : penta?.aoVivo ? t(`penta.ponta.${penta.aoVivo.cor}`) : ubAberto ? t('evento.nome') : null;
  const ultima = registro[0];
  const fixadosIds = new Set(fixados.map(f => f.id));

  const badge = (s: Secao) => {
    if (s === 'jogadores') return totalOnline == null ? null : <small>{contagemCurta(totalOnline, idioma)}</small>;
    if (s === 'mundo') return mobs.length ? <small>{mobs.length}</small> : null;
    if (s === 'eventos') return eventoAoVivo ? <i className="gmx-nav__vivo" title={eventoAoVivo} /> : null;
    if (s === 'registro') return registro.length ? <small>{registro.length}</small> : null;
    return null;
  };

  const paraQuem = itensPara ?? picked;
  const destinosItens = paraQuem && !paraQuem.you && !offline ? [paraQuem] : [];

  let corpo;
  if (secao === 'painel') {
    corpo = (
      <PainelGm totalOnline={totalOnline} naSala={sala?.total ?? null} mobs={mobs.length} hora={hora} eventoAoVivo={eventoAoVivo}
        registro={registro} onSecao={setSecao} onBuscar={() => setPaleta(true)} />
    );
  } else if (secao === 'jogadores') {
    corpo = (
      <div className="gmx-jog">
        <ListaDeJogadores busca={busca} setBusca={setBusca} achados={achados} sala={sala} fonte={fonte} setFonte={setFonte}
          recentes={recentes} fixados={fixados} totalOnline={totalOnline} picked={picked}
          onEscolher={escolher} onEscolherSalvo={j => void escolherSalvo(j)} />
        {picked
          ? <FichaDoJogador key={picked.id} alvo={picked} offline={offline} eu={eu} busy={busy} rodar={rodar} cidades={cidades}
              tileX={tileX} tileY={tileY} teleportando={picking === 'teleport'} fixado={fixadosIds.has(picked.id)}
              onFixar={() => alternarFixado(picked)} onIrAte={irAte} onTeleportar={a => void teleportar(a)} onCancelarTeleporte={cancelarPick}
              onMandar={mandar} onEditarSkills={a => onEditarSkills?.({ id: a.id, name: a.name })}
              onDarItem={a => { setItensPara(a); setSecao('itens'); }} skillsTick={skillsTick} />
          : <div className="gmx-vazio">{t('gm.x.escolhaJogador')}</div>}
      </div>
    );
  } else if (secao === 'mundo') {
    corpo = (
      <MundoGm busy={busy} rodar={rodar} mobs={mobs} catalogo={catalogo} targetMobId={targetMobId} invocando={picking === 'summon'}
        onInvocar={(d, a) => void invocar(d, a)} onCancelarInvocar={cancelarPick}
        onRemovido={id => { onRemoved?.(id); setMobs(l => l.filter(m => m.shortId !== id)); }} />
    );
  } else if (secao === 'eventos') {
    corpo = <EventosGm busy={busy} rodar={rodar} evAtual={evAtual} chefe={chefe} penta={penta} />;
  } else if (secao === 'itens') {
    corpo = (
      <div className="gmx-itens">
        <p className="gmx-h">{t('gm.item.titulo')}{itensPara && <small>{t('gm.x.itensPara', { nome: itensPara.name })}</small>}</p>
        <MarketGump active={active} tick={packTick} destinos={destinosItens} paraInicial={itensPara && !itensPara.you ? itensPara.id : undefined} buscaInicial={itemBusca}
          onChanged={txt => {
            onPackChanged?.();
            if (txt) registrar({ cat: t('gm.x.cat.itens'), txt });
          }} />
      </div>
    );
  } else if (secao === 'skins') {
    corpo = (
      <>
        <p className="gmx-h">{t('gm.skins.titulo')}</p>
        <GmSkins ativo={active} />
      </>
    );
  } else {
    corpo = (
      <div className="gmx-card">
        <div className="gmx-linha">
          <p className="gmx-h" style={{ margin: 0 }}>{t('gm.x.nav.registro')} <small>{t('gm.x.reg.soSessao')}</small></p>
          <span className="gmx-esp" />
          <button type="button" className="btn btn--tiny" disabled={registro.length === 0} onClick={() => void copiarRegistro()}>
            {copiado ? t('gm.x.reg.copiado') : t('gm.x.reg.copiar')}
          </button>
        </div>
        {registro.length === 0
          ? <p className="gmx-meta" style={{ marginTop: 8 }}>{t('gm.x.reg.vazio')}</p>
          : (
            <ul className="gmx-reg" style={{ marginTop: 6 }}>
              {registro.map(f => (
                <li key={f.id}>
                  <time>{f.hora}</time>
                  <span><i>{f.cat}</i>{f.txt}</span>
                  {f.desfazer
                    ? <button type="button" className="btn btn--tiny" disabled={busy} onClick={() => void desfazer(f)}>{t('gm.x.desfazer')}</button>
                    : <span />}
                </li>
              ))}
            </ul>
          )}
      </div>
    );
  }

  return (
    <div className="gmx">
      <div className="gmx__cab">
        <div className="gmx__eu">
          <span className="gmx__cargo">{role || 'GM'}</span>
          <b>{youName}</b>
          <span className="gmx__mono">
            {shard ? `${shard} · ` : ''}{canal != null ? `${t('mundo.canal', { n: canal })} · ` : ''}
            {tileX != null && tileY != null ? `${tileX}, ${tileY}` : ''}
          </span>
        </div>
        <div className="gmx__dir">
          {ocultoHabilitado && (
            <button type="button" className="gmx-chip" aria-pressed={oculto} disabled={busy} title={t('gm.oculto.regra')} onClick={alternarOculto}>
              <span className="gmx-sw" aria-hidden="true" />
              {oculto ? t('gm.oculto.ligado') : t('gm.oculto.desligado')}
              <kbd>Ctrl H</kbd>
            </button>
          )}
          <button type="button" className="gmx-chip" title={t('gm.hora.titulo')} onClick={() => setSecao('mundo')}>
            <span className="gmx__mono">{hora}</span>
          </button>
          <button type="button" className="gmx-chip" onClick={() => setPaleta(true)}>
            {t('gm.x.buscarAcao')} <kbd>Ctrl K</kbd>
          </button>
        </div>
      </div>

      <nav className="gmx__trilho" aria-label={t('gm.tituloLongo')}>
        {SECOES.map(s => (
          <span key={s} style={{ display: 'contents' }}>
            {s === 'registro' && <hr />}
            <button type="button" className="gmx-nav" aria-current={secao === s ? 'page' : undefined}
              title={t(`gm.x.nav.${s}`)} onClick={() => setSecao(s)}>
              <IconeGm nome={s} />
              <span>{t(`gm.x.nav.${s}`)}</span>
              {badge(s)}
            </button>
          </span>
        ))}
      </nav>

      <div className="gmx__corpo">{corpo}</div>

      <div className="gmx__rodape">
        <span>{t('gm.x.ultima')}</span>
        <span>{ultima ? `${ultima.hora} · ${ultima.txt}` : '—'}</span>
        <span className="gmx-esp" />
        {erro && <span className="gmx__erro" role="alert">{erro}</span>}
      </div>

      {avisos.length > 0 && (
        <div className="gmx-avisos" aria-live="polite">
          {avisos.map(a => (
            <div key={a.id} className="gmx-aviso">
              <span>{a.feito.txt}</span>
              {a.feito.desfazer && (
                <button type="button" className="btn btn--tiny" disabled={busy}
                  onClick={() => { const f = registro.find(x => x.id === a.id); if (f) void desfazer(f); }}>
                  {t('gm.x.desfazer')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {paleta && (
        <PaletaGm onFechar={() => setPaleta(false)} onSecao={s => setSecao(s)} onJogador={naPaleta}
          onOculto={() => { if (ocultoHabilitado) alternarOculto(); }}
          onItem={texto => { setItemBusca({ texto }); setSecao('itens'); }} />
      )}
    </div>
  );
}
